/* eslint-disable no-undef */
import BathsIcon from '@propertypal/shared/src/resources/icons/property/baths-icon.svg';
import BedsIcon from '@propertypal/shared/src/resources/icons/property/beds-icon.svg';
import ReceptionsIcon from '@propertypal/shared/src/resources/icons/property/receptions-icon.svg';
import trackEvent from '@propertypal/shared/src/services/analytics';
import { Property } from '@propertypal/shared/src/types/property';
import { getAwardFromContact, getDevelopmentAward } from '@propertypal/shared/src/utils/property/getAward';
import puralise from '@propertypal/shared/src/utils/puralise';
import Link from 'next/link';
import React, { CSSProperties, FunctionComponent, useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import Button from '../buttons/Button';
import { RowAlignCenter } from '../layout';
import { Heading, SubHeading, Text } from '../typography';
import EnergyRatingIcon from './EnergyRatingIcon';
import PropertyPrice from './PropertyPrice';
import {
  Container,
  KeyDetails,
  SummaryItem,
  Title,
  AgentBox,
  AgentLogo,
  AgentText,
  CallAgentBox,
  ShowPhoneNumber,
  AgentBoxFixed,
  AgentBoxFixedButtons,
  LicenceWarning,
  NameAndPSRTextWrapper,
  AwardLogo,
  AwardBox,
} from './PropertySummary.style';

interface Props {
  containerStyle?: CSSProperties;
  property: Property;
  onCallClick: () => void;
  onShowNumberClick: () => void;
  onEnquiryClick: () => void;
}

const PhoneNumberButton = (props: Pick<Props, 'onCallClick' | 'onShowNumberClick' | 'property'>) => {
  const onClick = () => {
    trackEvent('Detail Page', 'Click', 'Show Phone Number');
    props.onShowNumberClick();
  };

  return (
    <>
      <ShowPhoneNumber data-testid="showPhoneNumber">
        <Button containerStyle={{ height: 50 }} onClick={onClick} testID="showPhoneNumberButton" type="button">
          Show Phone Number
        </Button>
      </ShowPhoneNumber>

      <CallAgentBox>
        <Button containerStyle={{ height: 50 }} onClick={props.onCallClick} testID="callAgentButton" type="button">
          Call the Agent
        </Button>
      </CallAgentBox>
    </>
  );
};

const PropertySummary: FunctionComponent<Props> = (props) => {
  const { property } = props;
  const contact = property.contacts?.default;
  const contactBox = useRef<HTMLDivElement | null>(null);
  const theme = useTheme();
  const [showEnquiryBar, setShowEnquiryBar] = useState(false);
  const developerAward = getDevelopmentAward(property);
  const contactAward = getAwardFromContact(contact);
  const showKeyDetails =
    property.numBedrooms || property.numBathrooms || property.numReceptionRooms || property.epc || property.ber;

  // only add comma add end of address if address is more than 1 word
  // particularly will find this for ROI properties
  const addressAppend = () => {
    // handles potentially hyphenated addresses
    const words = props.property.displayAddressLine1.replace('-', '').match(/(\w+)/g);
    if (words && words.length > 1) return ',';
    return null;
  };

  const enquireNowClick = () => {
    trackEvent('Detail Page', 'Click', 'Enquire Now');
    props.onEnquiryClick();
  };

  useEffect(() => {
    const checkBelowThreshold: IntersectionObserverCallback = (entries) => {
      entries.forEach((entry) => {
        setShowEnquiryBar(entry.boundingClientRect.top < 0);
      });
    };

    const observer = new IntersectionObserver(checkBelowThreshold, {
      threshold: 1,
    });

    if (contactBox.current) {
      observer.observe(contactBox.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Container className="pp-property-summary">
        <div>
          <Heading fontWeight="600" data-testid="addressLine1">
            {props.property.displayAddressLine1}
            {addressAppend()}
          </Heading>

          <Text fontSize={24} color={theme.textLight}>
            {props.property.displayAddressLine2}
          </Text>

          <Text fontSize={18} mt={10} color={theme.textLight}>
            {props.property.briefText}
          </Text>

          <PropertyPrice
            fontSize={24}
            price={props.property.price}
            alternativeCurrencyPrice={props.property.alternativeCurrencyPrice}
            containerStyle={{ marginTop: 40 }}
            status={props.property.status}
            statusButton
          />

          {props.property.showHomeOpeningTimes && (
            <>
              <SubHeading mt={30} className="pp-show-home-title">
                Show Home Open Viewing:
              </SubHeading>

              <Text fontSize={18} mt={10} className="pp-show-home-text">
                {props.property.showHomeOpeningTimes}
              </Text>
            </>
          )}

          {showKeyDetails && (
            <KeyDetails>
              {!!property.numBedrooms && (
                <SummaryItem>
                  <BedsIcon color={theme.backgroundMid} className="pp-summary-icon" />

                  <Text ml={10}>
                    {property.numBedrooms} {puralise('Bedroom', 'Bedrooms', property.numBedrooms)}
                  </Text>
                </SummaryItem>
              )}

              {!!property.numBathrooms && (
                <SummaryItem>
                  <BathsIcon color={theme.backgroundMid} className="pp-summary-icon" />

                  <Text ml={10}>
                    {property.numBathrooms} {puralise('Bathroom', 'Bathrooms', property.numBathrooms)}
                  </Text>
                </SummaryItem>
              )}

              {!!property.numReceptionRooms && (
                <SummaryItem>
                  <ReceptionsIcon color={theme.backgroundMid} className="pp-summary-icon" />

                  <Text ml={10}>
                    {property.numReceptionRooms} {puralise('Reception', 'Receptions', property.numReceptionRooms)}
                  </Text>
                </SummaryItem>
              )}

              {(property.epc || property.ber) && (
                <SummaryItem>
                  <EnergyRatingIcon property={property} />
                </SummaryItem>
              )}
            </KeyDetails>
          )}

          {developerAward && (
            <AwardBox href={developerAward.url} target="_blank" rel="noreferrer">
              <p>{developerAward.text}</p>
              <img src={developerAward.imageUrl} alt={developerAward.text} />
            </AwardBox>
          )}
        </div>

        <AgentBox className="pp-contact-agent">
          <Title>Contact the Agent</Title>

          {contact ? (
            <>
              {contact.logos && contact.logos.standard && (
                <AgentLogo data-testid="agent-logo" src={contact.logos.standard} alt="Agent Logo" />
              )}
              <AgentText>{contact.organisation}</AgentText>
            </>
          ) : (
            <>
              {property.agents && property.agents.length === 1 && (
                <AgentLogo
                  data-testid="agent-logo"
                  src={property.agents[0].logo.standard || property.listingLogos[0].standard}
                />
              )}
              {property.agents && property.agents.length > 1 && <AgentText>Marketed by multiple agents</AgentText>}
            </>
          )}

          {property.account.psrLicenceNumber && property.countryCode === 'IRL' && (
            <AgentText>
              PSR Licence: {property.account.psrLicenceNumber}
              {property.account.psrLicenceNumber === 'NO LICENCE' && (
                <Link href="/psr-licence-number" passHref prefetch={false}>
                  <LicenceWarning>?</LicenceWarning>
                </Link>
              )}
            </AgentText>
          )}

          {contactAward && property.type === 'property' && !property.development && (
            <a href={contactAward.url} target="_blank" rel="noreferrer">
              <AwardLogo
                src={contactAward.imageUrl}
                alt={contactAward.label}
                style={{ marginTop: 15 }}
                data-testid="award-logo"
              />
            </a>
          )}

          {property.published && (
            <>
              <Button
                containerStyle={{ height: 50, marginTop: 30, marginBottom: 15 }}
                onClick={enquireNowClick}
                testID="enquireNowButton"
                type="button"
              >
                Enquire Now
              </Button>

              {contact && <PhoneNumberButton {...props} />}
            </>
          )}
        </AgentBox>

        <AgentBoxFixed
          className="pp-contact-agent"
          style={{ top: showEnquiryBar ? 0 : -60, visibility: showEnquiryBar ? 'visible' : 'hidden' }}
        >
          {contact && (
            <RowAlignCenter>
              {contact.logos && contact.logos && contact.logos.standard && (
                <img
                  src={contact.logos.standard}
                  alt={`${property.development ? 'Developer' : 'Agent'} Logo`}
                  style={{ marginRight: 15 }}
                  data-testid="agent-logo-fixed"
                />
              )}
              <NameAndPSRTextWrapper>
                <AgentText>{contact.organisation}</AgentText>
                {props.property.account.psrLicenceNumber && props.property.countryCode === 'IRL' && (
                  <AgentText>
                    PSR Licence: {props.property.account.psrLicenceNumber}
                    {props.property.account.psrLicenceNumber === 'NO LICENCE' && (
                      <Link href="/psr-licence-number" passHref prefetch={false}>
                        <LicenceWarning style={{ textAlign: 'center' }}>?</LicenceWarning>
                      </Link>
                    )}
                  </AgentText>
                )}
              </NameAndPSRTextWrapper>
            </RowAlignCenter>
          )}

          {!contact && (
            <RowAlignCenter>
              <AgentText>Marketed by multiple agents</AgentText>
            </RowAlignCenter>
          )}

          {property.published && (
            <AgentBoxFixedButtons>
              <Button
                containerStyle={{ height: 50, marginRight: 10 }}
                onClick={enquireNowClick}
                testID="enquireNowFixed"
                type="button"
              >
                Enquire Now
              </Button>

              {contact && <PhoneNumberButton {...props} />}
            </AgentBoxFixedButtons>
          )}
        </AgentBoxFixed>
      </Container>

      <div ref={contactBox} />
    </>
  );
};

export default PropertySummary;
